/* global globalThis */
import "./HistoryData.css";
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import axiosClient from "../../../../api-client/AxiosClient";
import { toast } from "react-toastify";
import { handleColumnResizeStart as resizeColumn } from "./TableUtils";

const HistoryData = () => {
  const showColumns = {
    sno: true,
    from: true,
    to: true,
    by: true,
    updatedOn: true,
    showMenu: false,
  };
  const location = useLocation();
  const { id } = useParams();
  const [dropdownValue, setDropdownValue] = useState(null);
  const [selectedCorporation, setSelectedCorporation] = useState(null);
  const [allFiles, setAllFiles] = useState([]); // Store all files from API
  const [auditData, setAuditData] = useState([]);
  const isMountedRef = useRef(false);
  const [pageData, setPageData] = useState({
    fileID: location.state?.fileID || null,
  });
  const [fileName, setFileName] = useState("");
  const [columnVisibilityMenu, setColumnVisibilityMenu] = useState(false);
  const [isFirstTimeLoaded, setIsFirstTimeLoaded] = useState(true);
  const visibilityMenuRef = useRef(null);
  const visibilityButtonRef = useRef(null);
  const [resizingColumn, setResizingColumn] = useState(null);
  const [columnSizes, setColumnSizes] = useState({});

  useEffect(() => {
    const storedCorporation = localStorage.getItem("selectedCorporation");
    if (storedCorporation) {
      const parsedCorporation = JSON.parse(storedCorporation);
      setSelectedCorporation(parsedCorporation);
    }
  }, []);

const formatUserName = (user) => {
  return user
    .split('@')[0]
    .replaceAll(/[._]/g, ' ')
    .replaceAll(/\b\w/g, l => l.toUpperCase());
};

const formatTimestamp = (timestamp) => {
  return new Date(timestamp).toLocaleString("en-US", {
    month: "numeric",
    day: "numeric", 
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: true
  });
};

/** Makes raw API values user-friendly: empty → "—", camelCase/snake_case → readable words */
const formatDisplayValue = (value) => {
  if (value == null || value === "") return "—";
  const str = String(value).trim();
  if (!str) return "—";
  // If it looks like a camelCase or snake_case identifier (no spaces, alphanumeric + underscore), make readable
  if (/^\w+$/.test(str) && !str.includes(" ")) {
    return str
      .replaceAll(/([A-Z])/g, " $1")
      .replaceAll("_", " ")
      .replace(/^\w/, (c) => c.toUpperCase())
      .trim();
  }
  return str;
};

const mapAuditItem = (item, index) => ({
  sno: index + 1,
  from: formatDisplayValue(item.updatedFrom),
  to: formatDisplayValue(item.updatedTo),
  by: item.user ? formatUserName(item.user) : "",
  updatedOn: item.timestamp ? formatTimestamp(item.timestamp) : "",
});

useEffect(() => {
  const fetchAuditData = async () => {
    try {
      const response = await axiosClient.get("/api/Audit/GetAudit", {
        params: {
          EntityType: 2,
          UserMappingFileId: id,
        },
      });
      if (response.status === 200 && Array.isArray(response.data)) {
        const withSno = response.data.map((item, index) => mapAuditItem(item, index));
        setAuditData(withSno);
      } else {
        toast.error("Invalid response from Audit API");
      }
      const storedFileName = sessionStorage.getItem("selectedFileName");
      if (storedFileName) {
        setFileName(storedFileName);
      }
    } catch (err) {
      console.error("Failed to fetch audit data:", err);
      toast.error("Failed to fetch audit data");
    } finally {
      setIsFirstTimeLoaded(false);
    }
  };

  fetchAuditData();
}, []);

  // 2. Listen for localStorage changes (for cross-tab synchronization)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "selectedCorporation") {
        const newCorporation = e.newValue ? JSON.parse(e.newValue) : null;
        setSelectedCorporation(newCorporation);

        // Clear current data and filter files for new corporation
        if (newCorporation) {
          setDropdownValue(null);
          // Filter files for this corporation
          filterFilesForCorporation(newCorporation.value);
        } else {
          setDropdownValue(null);
        }
      }
    };

    // Also listen for custom event for same-window changes
    const handleCustomStorageChange = (e) => {
      if (e.detail.key === "selectedCorporation") {
        const newCorporation = e.detail.newValue
          ? JSON.parse(e.detail.newValue)
          : null;
        setSelectedCorporation(newCorporation);

        if (newCorporation) {
          setDropdownValue(null);
          filterFilesForCorporation(newCorporation.value);
        } else {
          setDropdownValue(null);
        }
      }
    };

    globalThis.addEventListener("storage", handleStorageChange);
    globalThis.addEventListener("customStorageChange", handleCustomStorageChange);
    return () => {
      globalThis.removeEventListener("storage", handleStorageChange);
      globalThis.removeEventListener(
        "customStorageChange",
        handleCustomStorageChange
      );
    };
  }, []);

  // 3. Filter files when selectedCorporation changes
  useEffect(() => {
    if (selectedCorporation && allFiles.length > 0) {
      filterFilesForCorporation(selectedCorporation.value);
    }
  }, [selectedCorporation, allFiles]);

  // Handle clicks outside visibility menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        columnVisibilityMenu &&
        visibilityMenuRef.current &&
        visibilityButtonRef.current &&
        !visibilityMenuRef.current.contains(event.target) &&
        !visibilityButtonRef.current.contains(event.target)
      ) {
        setColumnVisibilityMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [columnVisibilityMenu]);

  const fetchAllFiles = async () => {
    try {
      // Fetch all files without corporation ID parameter
      const filesResponse = await axiosClient.get("/api/Mapper/GetMapperFiles");

      // Store all files in state
      setAllFiles(filesResponse.data);

      // If we have a selected corporation, filter the files
      if (selectedCorporation) {
        filterFilesForCorporation(selectedCorporation.value);
      }
    } catch (error) {
      console.error("Failed to fetch file list:", error);
      toast.error("Failed to fetch file list");
    }
  };

  // Initial data load
  useEffect(() => {
    if (isMountedRef.current) return;
    isMountedRef.current = true;

    const fetchData = async () => {
      // Fetch all files first
      await fetchAllFiles();
    };

    fetchData();
  }, []);

  const filterFilesForCorporation = (corporationId) => {
    // Filter files based on corporation ID
    const filteredFiles = allFiles.filter(
      (file) => file.corporationId === corporationId
    );

    const formattedFiles = filteredFiles.map((item) => ({
      value: item.userMappingFileId,
      label: item.uploadedObjDescription,
      id: item?.corporationId,
    }));

    if (pageData.fileID) {
      const selectedFile = formattedFiles.find(
        (opt) => opt.value === pageData.fileID
      );
      if (selectedFile) {
        setDropdownValue(selectedFile);
      } else {
        // If the previously selected file is not in the new corporation's files
        setDropdownValue(formattedFiles.length > 0 ? formattedFiles[0] : null);
      }
    } else if (formattedFiles.length > 0) {
      setDropdownValue(formattedFiles[0]);
    }
  };

  // Load data when dropdown value changes
  useEffect(() => {
    if (dropdownValue && !isFirstTimeLoaded) {
      setPageData((prev) => ({ ...prev, fileID: dropdownValue.value }));
      
    }
    if (dropdownValue && isFirstTimeLoaded) {
      setPageData((prev) => ({ ...prev, fileID: id }));
    }
  }, [dropdownValue]);

  // Filter based on global search only (you can extend with dropdown filter logic)
  const filteredData = auditData.filter((row) =>
    Object.values(row).join(" ").toLowerCase().includes("".toLowerCase())
  );

  const handleColumnResizeStart = (e, header) => {
    resizeColumn(e, header, setResizingColumn, columnSizes, setColumnSizes);
  };

  return (
    <div className="historydata-container">
      <div className="historydata-controls">
  <div style={{ 
    fontSize: '18px', 
    fontWeight: '500',
    color: '#333'
  }}>
History of <b>{fileName || 'File'}</b> Object

</div>
  <button
    className="historydata-back-btn"
    onClick={() => globalThis.history.back()}
  >
    Back
  </button>
</div>
      {/* Table */}
      <div className="historydata-table-wrap">
        <table className="historydata-table">
          <thead>
            <tr>
              {showColumns.sno && (
                <th
                  className="historydata-th"
                  style={{
                    width: columnSizes.sno || 60,
                    minWidth: 40,
                    position: "relative",
                  }}
                >
                  S.No
                  {" "}
                  <button
                    className="resizer"
                    aria-label="Resize S.No column"
                    onMouseDown={(e) =>
                      handleColumnResizeStart(e, {
                        id: "sno",
                        column: { getSize: () => columnSizes.sno || 60 },
                      })
                    }
                    style={{
                      background:
                        resizingColumn === "sno" ? "#2196f3" : "transparent",
                    }}
                  />
                </th>
              )}
              {showColumns.from && (
                <th
                  className="historydata-th"
                  style={{
                    width: columnSizes.from || 220,
                    minWidth: "50px",
                    position: "relative",
                  }}
                >
                  From
                  {" "}
                  <button
                    className="resizer"
                    aria-label="Resize From column"
                    onMouseDown={(e) =>
                      handleColumnResizeStart(e, {
                        id: "from",
                        column: { getSize: () => columnSizes.from || 220 },
                      })
                    }
                    style={{
                      background:
                        resizingColumn === "from" ? "#2196f3" : "transparent",
                    }}
                  />
                </th>
              )}
              {showColumns.to && (
                <th
                  className="historydata-th"
                  style={{
                    width: columnSizes.to || 220,
                    minWidth: "50px",
                    position: "relative",
                  }}
                >
                  To
                  {" "}
                  <button
                    className="resizer"
                    aria-label="Resize To column"
                    onMouseDown={(e) =>
                      handleColumnResizeStart(e, {
                        id: "to",
                        column: { getSize: () => columnSizes.to || 220 },
                      })
                    }
                    style={{
                      background:
                        resizingColumn === "to" ? "#2196f3" : "transparent",
                    }}
                  />
                </th>
              )}
              {showColumns.by && (
                <th
                  className="historydata-th"
                  style={{
                    width: columnSizes.by || 120,
                    minWidth: "50px",
                    position: "relative",
                  }}
                >
                  By
                  {" "}
                  <button
                    className="resizer"
                    aria-label="Resize By column"
                    onMouseDown={(e) =>
                      handleColumnResizeStart(e, {
                        id: "by",
                        column: { getSize: () => columnSizes.by || 120 },
                      })
                    }
                    style={{
                      background:
                        resizingColumn === "by" ? "#2196f3" : "transparent",
                    }}
                  />
                </th>
              )}
              {showColumns.updatedOn && (
                <th
                  className="historydata-th"
                  style={{
                    width: columnSizes.updatedOn || 120,
                    minWidth: 80,
                    position: "relative",
                  }}
                >
                  Updated On
                  {" "}
                  <button
                    className="resizer"
                    aria-label="Resize Updated On column"
                    onMouseDown={(e) =>
                      handleColumnResizeStart(e, {
                        id: "updatedOn",
                        column: {
                          getSize: () => columnSizes.updatedOn || 120,
                        },
                      })
                    }
                    style={{
                      background:
                        resizingColumn === "updatedOn"
                          ? "#2196f3"
                          : "transparent",
                    }}
                  />
                </th>
              )}
            </tr>
          </thead>
           <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td 
                  colSpan={5} 
                  style={{ 
                    textAlign: 'center', 
                    padding: '100px 20px',
                    color: '#666',
                    fontSize: '18px',
                    fontWeight: '500',
                    height: '400px',
                    verticalAlign: 'middle'
                  }}
                >
                  No mapping changes have been recorded yet
                </td>
              </tr>
            ) : (
              filteredData.map((row) => (
                <tr key={row.sno}>
                  {showColumns.sno && (
                    <td
                      className="historydata-td"
                      style={{ width: columnSizes.sno || 60, minWidth: 60 }}
                    >
                      {row.sno}
                    </td>
                  )}
                  {showColumns.from && (
                    <td
                      className="historydata-td"
                      style={{ width: columnSizes.from || 220, minWidth: 120 }}
                      title={row.from}
                    >
                      {row.from}
                    </td>
                  )}
                  {showColumns.to && (
                    <td
                      className="historydata-td"
                      style={{ width: columnSizes.to || 220, minWidth: 120 }}
                      title={row.to}
                    >
                      {row.to}
                    </td>
                  )}
                  {showColumns.by && (
                    <td
                      className="historydata-td"
                      style={{ width: columnSizes.by || 120, minWidth: 80 }}
                    >
                      {row.by}
                    </td>
                  )}
                  {showColumns.updatedOn && (
                    <td
                      className="historydata-td"
                      style={{
                        width: columnSizes.updatedOn || 120,
                        minWidth: 80,
                      }}
                    >
                      {row.updatedOn}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HistoryData;